/**
 * CesiumPropertyView.jsx — Phase C: Cesium ion 3D viewer for arena/stadium venues.
 *
 * Renders for: SPHERE, ALLEGIANT, TMOBILE, ATH_LV  (venueType: "arena" | "stadium")
 * Loaded lazily — CesiumJS (5-8MB) only fetches from CDN when first needed.
 *
 * Cesium ion: NOT Google Maps JS API — ADR-002 compliant.
 * 3D Tiles:   tries Google Photorealistic (ion asset 2275207) first;
 *             falls back to OSM Buildings (ion asset 96188).
 *
 * Index.jsx wires:
 *   • sets window.CESIUM_BASE_URL before injecting the Cesium script
 *   • passes cesiumReady=true once the CDN script fires onload
 *   • renders this component alongside PropertyMapView, toggled by isCesiumVenue()
 */

import { useEffect, useRef, useState } from "react";
import { PROPERTY_REGISTRY } from "../../../utils/PropertyRegistry.js";

// Venue types rendered here rather than in PropertyMapView (Leaflet 2-D).
// "airport" added — Google Photorealistic 3D Tiles render actual terminal
// buildings at JFK, BOS, LAS, ATL, LAX without any per-venue floor-plan config.
// The Leaflet polygon overlays (PropertyMapView) are superseded by the 3D view;
// sensePoints and walk/drive paths will be re-integrated as Cesium entities in Phase C+.
const CESIUM_VENUE_TYPES = new Set(["arena", "stadium", "airport"]);

/**
 * Returns true when the given property code should use CesiumPropertyView.
 * Exported so Index.jsx can gate CDN loading and the CSS tab toggle.
 */
export function isCesiumVenue(propertyCode) {
  if (!propertyCode) return false;
  const prop = PROPERTY_REGISTRY[propertyCode];
  return prop ? CESIUM_VENUE_TYPES.has(prop.venueType) : false;
}

// Cesium ion access token — scoped to this project.
// IMPORTANT: stored as a single unbroken string — JWT HMAC-SHA256 signatures
// are computed over the exact byte sequence; splitting across concatenated
// string literals risks a one-character corruption that silently invalidates
// the signature and causes all ion asset requests to fail with 401.
// eslint-disable-next-line max-len
const CESIUM_ION_TOKEN = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJub25jZSI6IlNXdzIzQ2RlMW1vc3lEekIiLCJqdGkiOiIxZmIyNjhlMy0yM2UzLTRhZTQtODVmOS00ZTJjN2E0M2YzMWIiLCJpZCI6NTIwMjc5LCJpc3MiOiJodHRwczovL2FwaS5jZXNpdW0uY29tIiwiYXVkIjoidW5kZWZpbmVkX2RlZmF1bHQiLCJpYXQiOjE3OTE3MTAwNzR9.wtZ4hkwoy2lw37AAQN4GG5DZ-7y5RTf6sD29GCoi0p4";

// Camera config per venue — { alt (metres above venue center), pitch (degrees,
//   stored positive, negated on setView so camera looks DOWN), heading (degrees
//   clockwise from north: 0=N, 90=E, 180=S, 270=W) }.
// setView orientation convention: pitch = 0° → horizon; negative → ground.
// We store pitch as positive and negate it in positionCameraAtVenue.
const VENUE_CAMERA = {
  // ── Las Vegas arenas / stadiums ──────────────────────────────────────────
  SPHERE:    { alt: 2800, pitch: 55, heading:   0 },  // Sphere — north-up overview
  ALLEGIANT: { alt: 1200, pitch: 52, heading: 300 },  // NW approach — full stadium footprint visible
  TMOBILE:   { alt: 2500, pitch: 55, heading:   0 },  // T-Mobile Arena — north-up
  ATH_LV:    { alt: 2800, pitch: 55, heading:   0 },  // Athletics 2028 site — north-up

  // ── New York — JFK ───────────────────────────────────────────────────────
  JFK_T5:   { alt:  700, pitch: 60, heading:  10 },   // T5 JetBlue — slight NNE tilt
  NAT_JFK:  { alt:  500, pitch: 60, heading:   0 },

  // ── Boston — BOS ─────────────────────────────────────────────────────────
  BOS_TC:   { alt:  600, pitch: 60, heading:   0 },   // Terminal C
  NAT_BOS:  { alt:  500, pitch: 60, heading:   0 },

  // ── Las Vegas — Harry Reid Airport ───────────────────────────────────────
  LAS_T3:   { alt:  700, pitch: 60, heading: 350 },   // T3 / D Gates — near-north
  NAT_LAS:  { alt:  500, pitch: 60, heading:   0 },

  // ── Atlanta — Hartsfield-Jackson ─────────────────────────────────────────
  ATL:         { alt: 1800, pitch: 50, heading:   0 }, // Full airport campus
  ATL_DELTA_S: { alt:  700, pitch: 60, heading: 355 }, // South Terminal
  ATL_INTL:    { alt:  700, pitch: 60, heading:   0 }, // International Terminal E/F

  // ── Los Angeles — LAX ────────────────────────────────────────────────────
  LAX:      { alt: 1800, pitch: 50, heading: 270 },   // Full campus — west-facing approach
  LAX_TBIT: { alt:  700, pitch: 60, heading: 270 },   // Tom Bradley International
  NAT_LAX:  { alt:  500, pitch: 60, heading:   0 },
};

// Alert-color → hex map (mirrors JourneyQueueSvgIcons.js ALERT_COLORS)
const ALERT_HEX = {
  green:  "#10B981",
  amber:  "#F59E0B",
  red:    "#EF4444",
  purple: "#7C3AED",
};

// ── Component ──────────────────────────────────────────────────────────────────

export default function CesiumPropertyView({
  sessions,
  queueRegistryRef,
  isActive,
  selectedProperty,
  cesiumReady,
}) {
  const containerRef       = useRef(null);
  const viewerRef          = useRef(null);
  const sessionEntitiesRef = useRef(new Map());
  const tilesetRef         = useRef(null);

  // Keep a ref to selectedProperty so the cesiumReady effect can read the
  // CURRENT value without a stale closure — React effects capture the value
  // at the time they're scheduled, not when the dep change fires.
  const selectedPropertyRef = useRef(selectedProperty);
  useEffect(() => { selectedPropertyRef.current = selectedProperty; });

  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [buildingsStatus, setBuildingsStatus] = useState("loading");
  const [buildingsLabel,  setBuildingsLabel]  = useState("3D Tiles");
  // Live camera state — shown in the debug panel so we can see what Cesium is doing
  const [camInfo, setCamInfo] = useState(null);

  // ── Recalculate canvas size when this tab is revealed ─────────────────────
  useEffect(() => {
    if (isActive && viewerRef.current) {
      try { viewerRef.current.resize(); } catch { /* viewer may not be ready */ }
    }
  }, [isActive]);

  // ── Initialise Cesium Viewer ───────────────────────────────────────────────
  useEffect(() => {
    if (!cesiumReady || !containerRef.current || viewerRef.current) return;

    const Cesium = window.Cesium;
    if (!Cesium) {
      setError("Cesium global not found — CDN may not have loaded.");
      setLoading(false);
      return;
    }

    Cesium.Ion.defaultAccessToken = CESIUM_ION_TOKEN;

    let viewer;
    try {
      viewer = new Cesium.Viewer(containerRef.current, {
        animation:            false,
        baseLayerPicker:      false,
        fullscreenButton:     false,
        geocoder:             false,
        homeButton:           false,
        infoBox:              false,
        sceneModePicker:      false,
        selectionIndicator:   false,
        timeline:             false,
        navigationHelpButton: false,
      });

      viewer.scene.globe.enableLighting = false;

      // ── Imagery: Cesium World Imagery (ion asset 2, free on all ion accounts) ──
      // Asset 2 is Cesium's curated high-resolution aerial/satellite composite —
      // much sharper than the flat OSM raster tile layer, includes Las Vegas
      // aerial detail sufficient to see Allegiant's roof and Raiders Plaza.
      // Falls back to OSM if the ion token can't reach asset 2.
      // useEffect callbacks must be synchronous, so imagery load runs in an IIFE.
      viewer.imageryLayers.removeAll();
      (async () => {
        try {
          const ionImagery = await Cesium.IonImageryProvider.fromAssetId(2);
          if (!viewer.isDestroyed()) viewer.imageryLayers.addImageryProvider(ionImagery);
        } catch {
          // ion asset 2 unavailable — fall back to OSM (always works, no credentials)
          try {
            if (!viewer.isDestroyed()) {
              viewer.imageryLayers.addImageryProvider(
                new Cesium.UrlTemplateImageryProvider({
                  url:          "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
                  credit:       "© OpenStreetMap contributors",
                  maximumLevel: 19,
                })
              );
            }
          } catch (osmErr) {
            console.warn("[CesiumPropertyView] imagery providers both failed:", osmErr);
          }
        }
      })();

      // ── Globe depth / collision ───────────────────────────────────────────────
      // depthTestAgainstTerrain: geometry below the terrain surface is hidden
      //   (buildings, entities, and camera all correctly occlude against terrain).
      // enableCollisionDetection: prevents the camera from clipping underground
      //   and looking back up through the earth at the star skybox.
      viewer.scene.globe.depthTestAgainstTerrain = true;
      viewer.scene.screenSpaceCameraController.enableCollisionDetection = true;

      viewerRef.current = viewer;
    } catch (e) {
      console.error("[CesiumPropertyView] Viewer init failed:", e);
      setError("Cesium viewer failed to initialise.");
      setLoading(false);
      return;
    }

    setLoading(false);

    // ── Position camera via postRender one-shot ───────────────────────────────
    // postRender fires exactly once after Cesium has rendered its FIRST frame —
    // the scene, globe, and camera system are fully ready at this point.
    // This is more reliable than rAF (which fires before Cesium's render loop)
    // or React state (which may batch/miss when selectedProperty hasn't changed).
    const removePostRender = viewer.scene.postRender.addEventListener(() => {
      removePostRender(); // one-shot — remove immediately after first fire
      const code = selectedPropertyRef.current;
      if (code && !viewer.isDestroyed()) positionCameraAtVenue(viewer, code);
    });

    // Live camera info poll — updates the debug panel every 800ms
    const camPoll = setInterval(() => {
      const v = viewerRef.current;
      if (!v || v.isDestroyed()) return;
      try {
        const Cesium = window.Cesium;
        const pos  = v.camera.positionCartographic;
        const alt  = (pos.height / 1000).toFixed(1);
        const hdg  = (Cesium.Math.toDegrees(v.camera.heading)).toFixed(1);
        const pit  = (Cesium.Math.toDegrees(v.camera.pitch)).toFixed(1);
        const lat  = (Cesium.Math.toDegrees(pos.latitude)).toFixed(4);
        const lng  = (Cesium.Math.toDegrees(pos.longitude)).toFixed(4);
        setCamInfo({ alt, hdg, pit, lat, lng });
      } catch { /* ignore if viewer is mid-destroy */ }
    }, 800);

    // Try 3D buildings non-blocking
    tryLoadBuildings(viewer, tilesetRef, setBuildingsStatus, setBuildingsLabel);

    return () => {
      clearInterval(camPoll);
      try {
        if (viewer && !viewer.isDestroyed()) viewer.destroy();
      } catch { /* already destroyed */ }
      viewerRef.current = null;
    };
  }, [cesiumReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reposition camera when selectedProperty changes AFTER viewer exists ────
  // (Handles the case where the viewer is already up and the user picks a
  //  different Cesium venue, e.g. ALLEGIANT → SPHERE.)
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !selectedProperty) return;
    positionCameraAtVenue(viewer, selectedProperty);
  }, [selectedProperty]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Session markers as Cesium billboard entities ───────────────────────────
  useEffect(() => {
    const viewer = viewerRef.current;
    const Cesium = window.Cesium;
    if (!viewer || !Cesium || !selectedProperty) return;

    const venueSessions = sessions.filter(
      (s) => s.scope === "property" && s.property_code === selectedProperty
    );
    const seenIds = new Set();

    venueSessions.forEach((session) => {
      const pos = session.position;
      if (!pos || !isFinite(Number(pos.lat)) || !isFinite(Number(pos.lng))) return;

      seenIds.add(session.id);
      const color  = ALERT_HEX[session.alert_color] || ALERT_HEX.green;
      const latNum = Number(pos.lat);
      const lngNum = Number(pos.lng);

      const existing = sessionEntitiesRef.current.get(session.id);
      if (existing) {
        // Move existing entity
        try {
          existing.position = Cesium.Cartesian3.fromDegrees(lngNum, latNum, 10);
        } catch { /* entity may have been removed */ }
      } else {
        // New entity
        try {
          const entity = viewer.entities.add({
            position: Cesium.Cartesian3.fromDegrees(lngNum, latNum, 10),
            billboard: {
              image:           buildSessionPin(color),
              width:           32,
              height:          32,
              verticalOrigin:  Cesium.VerticalOrigin.BOTTOM,
              heightReference: Cesium.HeightReference.RELATIVE_TO_GROUND,
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
            },
            label: {
              text:            session.passenger?.name || "Guest",
              font:            "bold 12px system-ui",
              fillColor:       Cesium.Color.WHITE,
              outlineColor:    Cesium.Color.BLACK,
              outlineWidth:    2,
              style:           Cesium.LabelStyle.FILL_AND_OUTLINE,
              pixelOffset:     new Cesium.Cartesian2(0, -40),
              heightReference: Cesium.HeightReference.RELATIVE_TO_GROUND,
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
            },
          });
          sessionEntitiesRef.current.set(session.id, entity);
        } catch (e) {
          console.warn("[CesiumPropertyView] entity add failed:", e);
        }
      }
    });

    // Remove stale entities (session ended or scope changed)
    sessionEntitiesRef.current.forEach((entity, id) => {
      if (!seenIds.has(id)) {
        try { viewer.entities.remove(entity); } catch { /* already gone */ }
        sessionEntitiesRef.current.delete(id);
      }
    });
  }, [sessions, selectedProperty]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const focusedProp  = selectedProperty ? PROPERTY_REGISTRY[selectedProperty] : null;
  const propSessions = sessions.filter((s) => s.scope === "property").length;

  return (
    <div style={{ position: "relative", flex: 1, background: "#07090F" }}>

      {/* Cesium 3-D canvas — full bleed inside parent flex cell */}
      <div
        ref={containerRef}
        style={{ width: "100%", height: "100%", minHeight: 480 }}
      />

      {/* Loading overlay */}
      {loading && !error && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 10,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          background: "rgba(7,9,15,0.88)", color: "#9CA3AF",
          pointerEvents: "none",
        }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🌐</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Loading 3D Buildings…</div>
          <div style={{ fontSize: 11, marginTop: 4, color: "#6B7280" }}>
            Cesium ion · {focusedProp?.label || "Venue"}
          </div>
        </div>
      )}

      {/* Viewer init error overlay (only for hard failures — missing Cesium global, WebGL crash) */}
      {error && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 10,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          background: "rgba(7,9,15,0.92)", color: "#EF4444",
          pointerEvents: "none",
        }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 13, fontWeight: 600, maxWidth: 280, textAlign: "center" }}>{error}</div>
          <div style={{ fontSize: 11, marginTop: 6, color: "#6B7280" }}>
            CesiumJS CDN or WebGL unavailable
          </div>
        </div>
      )}

      {/* Venue label chip (top-left) */}
      {focusedProp && (
        <div style={{
          position: "absolute", top: 12, left: 12, zIndex: 9,
          background: "rgba(7,9,15,0.88)",
          border: "1px solid #7C3AED",
          borderRadius: 8, padding: "5px 10px",
          fontSize: 11, fontWeight: 700, color: "#C4B5FD",
          display: "flex", alignItems: "center", gap: 6,
          pointerEvents: "none",
        }}>
          <span style={{ fontSize: 16 }}>{focusedProp.emoji || "🏟"}</span>
          <span>{focusedProp.label}</span>
          <span style={{
            fontSize: 10, padding: "1px 6px",
            borderRadius: 8,
            background: "rgba(124,58,237,0.25)",
            color: "#A78BFA",
            border: "1px solid rgba(124,58,237,0.4)",
            fontWeight: 600,
          }}>3D</span>
        </div>
      )}

      {/* Legend (top-right) */}
      <div style={{
        position: "absolute", top: 12, right: 12, zIndex: 9,
        background: "rgba(15,22,41,0.92)", borderRadius: 8,
        border: "1px solid #1E2D52", padding: "8px 12px",
        fontSize: 11, color: "#9CA3AF",
        pointerEvents: "none",
      }}>
        <div style={{ fontWeight: 700, marginBottom: 6, color: "#E0E0F0" }}>
          🏟 Venue — 3D View
        </div>
        <div style={{ color: "#7C3AED", marginBottom: 3 }}>Cesium ion</div>
        <div style={{
          fontSize: 10, marginBottom: 2,
          color: buildingsStatus === "loaded"      ? "#10B981"
               : buildingsStatus === "unavailable" ? "#6B7280"
               : "#4A5568",
        }}>
          {buildingsStatus === "loading"     ? "⟳ Loading buildings…"
         : buildingsStatus === "loaded"      ? `✓ ${buildingsLabel}`
         : "⊘ Terrain only"}
        </div>
        {focusedProp && (
          <div style={{ fontSize: 10, color: "#9CA3AF", textTransform: "capitalize" }}>
            {focusedProp.venueType === "arena" ? "🏟 Arena" : "🏈 Stadium"}
          </div>
        )}
        {propSessions > 0 && (
          <div style={{
            marginTop: 8, paddingTop: 6, borderTop: "1px solid #1E2D52",
            color: "#E0E0F0", fontWeight: 700,
          }}>
            {propSessions} active {propSessions === 1 ? "session" : "sessions"}
          </div>
        )}
      </div>

      {/* Camera debug panel (bottom-left) — live orientation readout every 800ms */}
      {camInfo && (
        <div style={{
          position: "absolute", bottom: 12, left: 12, zIndex: 9,
          background: "rgba(7,9,15,0.90)",
          border: "1px solid #1E3A5F",
          borderRadius: 8, padding: "8px 12px",
          fontSize: 10, color: "#9CA3AF",
          fontFamily: "monospace",
          lineHeight: "1.7",
          pointerEvents: "none",
        }}>
          <div style={{ fontWeight: 700, color: "#60A5FA", marginBottom: 4, fontSize: 11, fontFamily: "system-ui" }}>
            📡 Camera
          </div>
          <div><span style={{ color: "#6B7280" }}>lat  </span><span style={{ color: "#E0E0F0" }}>{camInfo.lat}°</span></div>
          <div><span style={{ color: "#6B7280" }}>lng  </span><span style={{ color: "#E0E0F0" }}>{camInfo.lng}°</span></div>
          <div><span style={{ color: "#6B7280" }}>alt  </span><span style={{ color: "#34D399" }}>{camInfo.alt} km</span></div>
          <div>
            <span style={{ color: "#6B7280" }}>pitch </span>
            <span style={{
              color: Number(camInfo.pit) < -10 ? "#34D399" // looking down → green
                   : Number(camInfo.pit) > 10  ? "#F87171" // looking up → red
                   : "#FBBF24",                             // near-horizon → yellow
            }}>
              {camInfo.pit}°
            </span>
            <span style={{ color: "#4B5563", marginLeft: 4 }}>
              {Number(camInfo.pit) < -45 ? "(↓ ground)"
               : Number(camInfo.pit) > 45  ? "(↑ sky)"
               : "(→ horizon)"}
            </span>
          </div>
          <div><span style={{ color: "#6B7280" }}>hdg  </span><span style={{ color: "#E0E0F0" }}>{camInfo.hdg}°</span></div>
        </div>
      )}

      {/* Reset View button (bottom-right) */}
      {!loading && !error && (
        <button
          onClick={() => {
            const v = viewerRef.current;
            if (v && !v.isDestroyed() && selectedProperty) {
              positionCameraAtVenue(v, selectedProperty);
            }
          }}
          style={{
            position: "absolute", bottom: 12, right: 12, zIndex: 9,
            background: "rgba(124,58,237,0.85)",
            border: "1px solid #7C3AED",
            borderRadius: 8,
            padding: "7px 14px",
            fontSize: 12, fontWeight: 700, color: "#F5F3FF",
            cursor: "pointer",
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          📍 Reset View
        </button>
      )}

    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Position the Cesium camera overhead the given venue using camera.lookAt +
 * HeadingPitchRange — the most reliable approach in Cesium 1.x.
 *
 * camera.lookAt(target, HeadingPitchRange(heading, pitch, range)):
 *   heading = 0      → north
 *   pitch   = +π/2   → camera directly above target, looking straight down ✓
 *   pitch   = +65°   → camera 65° above horizon (bird's-eye with slight tilt) ✓
 *   pitch   = negative → camera UNDERGROUND, looking up at sky ✗
 *   range            → distance from target to camera in metres
 *
 * After lookAt we call lookAtTransform(IDENTITY) to release the tracking
 * constraint so the user can pan/zoom freely.
 *
 * Why NOT camera.flyTo({ orientation: { pitch } })?
 *   flyTo uses heading/pitch/roll relative to a local ENU frame and is correct,
 *   but the animation starts from the globe's default view (pointing at Earth
 *   from space, pitch ≈ 0). If flyTo is interrupted (another setState, resize,
 *   or first-frame timing) the camera parks at mid-animation — often looking up.
 *   lookAt sets the final position immediately with no animation path to fail.
 */
function positionCameraAtVenue(viewer, propertyCode) {
  const Cesium = window.Cesium;
  if (!Cesium || !viewer || viewer.isDestroyed()) return;

  const prop = PROPERTY_REGISTRY[propertyCode];
  if (!prop) return;

  const camCfg = VENUE_CAMERA[propertyCode] || { alt: 800, pitch: 55, heading: 0 };

  // camera.setView: instant, no animation to interrupt.
  // pitch stored positive in VENUE_CAMERA; negated here → camera looks DOWN.
  // heading is degrees clockwise from north (0=N, 90=E, 270=W).
  try {
    viewer.camera.setView({
      destination: Cesium.Cartesian3.fromDegrees(prop.lng, prop.lat, camCfg.alt),
      orientation: {
        heading: Cesium.Math.toRadians(camCfg.heading ?? 0),
        pitch:   Cesium.Math.toRadians(-camCfg.pitch),
        roll:    0,
      },
    });
    console.log(
      `[CesiumPropertyView] setView → ${propertyCode} ` +
      `alt=${camCfg.alt}m pitch=${-camCfg.pitch}° hdg=${camCfg.heading ?? 0}°`
    );
  } catch (e) {
    console.warn("[CesiumPropertyView] positionCameraAtVenue failed:", e);
  }
}

/**
 * Build a small PNG data-URI for a Cesium billboard (coloured circle pin).
 * Canvas API is always available in the browser context where Cesium runs.
 */
function buildSessionPin(hexColor) {
  const size = 32;
  const c    = document.createElement("canvas");
  c.width    = size;
  c.height   = size;
  const ctx  = c.getContext("2d");
  const cx   = size / 2, cy = size / 2, r = size / 2 - 3;

  // Outer ring
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, 2 * Math.PI);
  ctx.strokeStyle = hexColor;
  ctx.lineWidth   = 3;
  ctx.stroke();

  // Dark inner fill
  ctx.beginPath();
  ctx.arc(cx, cy, r - 4, 0, 2 * Math.PI);
  ctx.fillStyle = "rgba(7,9,15,0.85)";
  ctx.fill();

  // Bright centre dot
  ctx.beginPath();
  ctx.arc(cx, cy, 4, 0, 2 * Math.PI);
  ctx.fillStyle = hexColor;
  ctx.fill();

  return c.toDataURL();
}

/**
 * Try to load 3D building tilesets non-blocking.
 *
 * Strategy — each step falls through silently on failure:
 *   1. createGooglePhotorealistic3DTileset — requires ion account + Google ToS accepted
 *   2. createOsmBuildingsAsync             — Cesium's convenience wrapper (Cesium 1.104+)
 *   3. Cesium3DTileset.fromIonAssetId(96188) — direct ion asset call
 *
 * On all failures the viewer still shows the 3D globe with terrain + imagery.
 * The legend updates to "Terrain only" — no error overlay blocks the view.
 *
 * To enable 3D buildings: visit ion.cesium.com and add "Cesium OSM Buildings"
 * (asset 96188) to your account, or accept Google's ToS for Photorealistic tiles.
 */
async function tryLoadBuildings(viewer, tilesetRef, setBuildingsStatus, setBuildingsLabel) {
  if (!viewer || viewer.isDestroyed()) return;
  const Cesium = window.Cesium;

  // 1. Google Photorealistic 3D Tiles
  if (typeof Cesium.createGooglePhotorealistic3DTileset === "function") {
    try {
      const tileset = await Cesium.createGooglePhotorealistic3DTileset();
      if (viewer.isDestroyed()) return;
      viewer.scene.primitives.add(tileset);
      tilesetRef.current = tileset;
      setBuildingsLabel("Google Photorealistic");
      setBuildingsStatus("loaded");
      return;
    } catch { /* falls through */ }
  }

  // 2. createOsmBuildingsAsync (Cesium 1.104+ convenience wrapper)
  if (typeof Cesium.createOsmBuildingsAsync === "function") {
    try {
      const tileset = await Cesium.createOsmBuildingsAsync();
      if (viewer.isDestroyed()) return;
      viewer.scene.primitives.add(tileset);
      tilesetRef.current = tileset;
      setBuildingsLabel("OSM Buildings");
      setBuildingsStatus("loaded");
      return;
    } catch { /* falls through */ }
  }

  // 3. Direct ion asset 96188
  try {
    let tileset;
    if (typeof Cesium.Cesium3DTileset?.fromIonAssetId === "function") {
      tileset = await Cesium.Cesium3DTileset.fromIonAssetId(96188);
    } else {
      const resource = await Cesium.IonResource.fromAssetId(96188);
      tileset = new Cesium.Cesium3DTileset({ url: resource });
    }
    if (viewer.isDestroyed()) return;
    viewer.scene.primitives.add(tileset);
    tilesetRef.current = tileset;
    setBuildingsLabel("OSM Buildings");
    setBuildingsStatus("loaded");
    return;
  } catch (e) {
    console.info(
      "[CesiumPropertyView] 3D buildings unavailable — ion assets not accessible. " +
      "Showing terrain + imagery. To enable: visit ion.cesium.com → add asset 96188 " +
      "(Cesium OSM Buildings) to your account.",
      e
    );
  }

  // All attempts failed — viewer shows terrain + imagery (still useful for venue location)
  setBuildingsStatus("unavailable");
}
